import { useEffect, useMemo, useState } from "react";

import type { Node, SessionApi } from "../lib/api";

/** Choose which files the assistant sees.
 *
 * It is told the workspace's file list every turn, but it cannot open anything
 * — reading a file it chose would need a loop, and this design does not have
 * one. So what it can actually read is exactly what is checked here, which
 * makes this control part of the feature rather than a convenience.
 */
export function AttachPicker({
  api,
  attached,
  onChange,
  onClose,
}: {
  api: SessionApi;
  attached: string[];
  onChange: (paths: string[]) => void;
  onClose: () => void;
}) {
  const [files, setFiles] = useState<Node[] | null>(null);
  const [filter, setFilter] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .listFiles()
      .then(setFiles)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [api]);

  const shown = useMemo(() => {
    const all = files ?? [];
    const needle = filter.trim().toLowerCase();
    return needle ? all.filter((f) => f.path.toLowerCase().includes(needle)) : all;
  }, [files, filter]);

  function toggle(path: string) {
    onChange(
      attached.includes(path) ? attached.filter((p) => p !== path) : [...attached, path],
    );
  }

  return (
    <div className="attach-picker">
      <div className="attach-head">
        <input
          autoFocus
          placeholder="Filter files…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <button className="link" onClick={onClose}>
          done
        </button>
      </div>
      {error && <div className="attach-error">{error}</div>}
      <div className="attach-list">
        {files === null && <div className="assist-hint">loading…</div>}
        {files !== null && shown.length === 0 && <div className="assist-hint">nothing matches</div>}
        {shown.map((file) => (
          <label key={file.path} className="attach-row">
            <input
              type="checkbox"
              checked={attached.includes(file.path)}
              onChange={() => toggle(file.path)}
            />
            <span className="attach-path">{file.path}</span>
            <span className="attach-size">
              {file.size !== null ? `${Math.max(1, Math.round((file.size ?? 0) / 1024))}k` : ""}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}
